import React, { useState, useEffect } from 'react';
import { useNotifications } from '../../../contexts/notification';
import styled from 'styled-components';
import { 
  Card, 
  CardWithHeader, 
  CardHeader, 
  CardFooter,
  CardGrid
} from '../../../styles/components/common/cards';
import { 
  Input, 
  Textarea, 
  Label, 
  FormGroup, 
  ErrorMessage 
} from '../../../styles/components/common/forms';
import { 
  Button, 
  SecondaryButton, 
  IconButton 
} from '../../../styles/components/common/buttons';
import { glassDeep, glassMedium, glassHover } from '../../../styles/components/common/glassmorphism';

// Styled components for the report form
const ReportForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormSection = styled.div`
  ${glassMedium}
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  
  h3 {
    color: rgba(255, 255, 255, 0.9);
    margin-top: 0;
    margin-bottom: 1.25rem;
    font-size: 1.25rem;
    position: relative;
    
    &:after {
      content: '';
      position: absolute;
      bottom: -0.5rem;
      left: 0;
      width: 3rem;
      height: 2px;
      background: linear-gradient(90deg, rgba(99, 102, 241, 0.7), transparent);
      border-radius: 1px;
    }
  }
`;

const InternshipInfo = styled.div`
  ${glassDeep}
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  
  h3 {
    color: rgba(255, 255, 255, 0.9);
    margin-top: 0;
    margin-bottom: 1.25rem;
    font-size: 1.25rem;
    position: relative;
    
    &:after {
      content: '';
      position: absolute;
      bottom: -0.5rem;
      left: 0;
      width: 3rem;
      height: 2px;
      background: linear-gradient(90deg, rgba(99, 102, 241, 0.7), transparent);
      border-radius: 1px;
    }
  }
  
  .info-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
    
    p {
      margin: 0.5rem 0;
      color: rgba(255, 255, 255, 0.8);
      
      strong {
        color: rgba(255, 255, 255, 0.95);
        margin-right: 0.5rem;
      }
    }
  }
`;

const SkillsContainer = styled.div`
  margin-bottom: 1.5rem;
`;

const SkillsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
`;

const SkillTag = styled.div`
  background: rgba(99, 102, 241, 0.3);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(99, 102, 241, 0.5);
  }
  
  button {
    background: transparent;
    border: none;
    color: white;
    cursor: pointer;
    font-size: 1rem;
    display: flex;
    align-items: center;
    padding: 0;
    margin-left: 0.25rem;
    
    &:hover {
      color: rgba(255, 255, 255, 0.7);
    }
  }
`;

const SkillInputContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
`;

const SuggestedSkills = styled.div`
  margin-top: 1rem;
  
  p {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.875rem;
    margin-bottom: 0.5rem;
  }
  
  .suggested-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
`;

const SuggestedSkillButton = styled.button`
  background: rgba(15, 23, 42, 0.5);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(15, 23, 42, 0.7);
    border-color: rgba(255, 255, 255, 0.2);
  }
`;

const AttachmentsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const AttachmentItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  background: rgba(15, 23, 42, 0.3);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  
  .file-info {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    
    .file-icon {
      color: rgba(99, 102, 241, 0.7);
      font-size: 1.25rem;
    }
    
    .file-details {
      strong {
        display: block;
        color: rgba(255, 255, 255, 0.9);
        font-size: 0.875rem;
      }
      
      span {
        color: rgba(255, 255, 255, 0.6);
        font-size: 0.75rem;
      }
    }
  }
  
  button {
    background: transparent;
    border: none;
    color: rgba(239, 68, 68, 0.8);
    cursor: pointer;
    font-size: 1rem;
    padding: 0.25rem;
    
    &:hover {
      color: rgba(239, 68, 68, 1);
    }
  }
`;

const FileUploadButton = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background: rgba(99, 102, 241, 0.3);
  color: white;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(99, 102, 241, 0.5);
  }
  
  svg {
    font-size: 1.25rem;
  }
`;

const FormActions = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const CharacterCount = styled.small`
  display: block;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.75rem;
  margin-top: 0.25rem;
  text-align: right;
`;

const InternshipReport = ({ internship, onClose, onSave, existingReport }) => {
  const { addNotification } = useNotifications();
  
  const [report, setReport] = useState({
    title: '',
    summary: '',
    learningOutcomes: '',
    challenges: '',
    solutions: '',
    skillsGained: [],
    futureApplications: '',
    advisorFeedback: '',
    attachments: [],
    isFinalized: false
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [skillInput, setSkillInput] = useState('');
  
  // Check if the form should be in read-only mode (for accepted or finalized reports)
  const isReadOnly = existingReport?.status === 'accepted' || existingReport?.isFinalized || report.isFinalized;
  
  // Skills list suggestions
  const suggestedSkills = [
    'JavaScript', 'React', 'Node.js', 'Python', 'Java', 'SQL', 'Data Analysis',
    'UI/UX Design', 'Project Management', 'Agile', 'Communication', 'Teamwork',
    'Problem Solving', 'Critical Thinking', 'Time Management', 'Leadership'
  ];
  
  // Initialize form with existing report if provided
  useEffect(() => {
    if (existingReport) {
      setReport(existingReport);
    } else if (internship) {
      // Ensure internship has all required properties before initializing
      if (internship.position && internship.company && internship.company.name) {
        // Initialize title with internship info
        setReport(prev => ({
          ...prev,
          title: `Internship Report: ${internship.position} at ${internship.company.name}`
        }));
      } else {
        console.error('Internship data is incomplete:', internship);
        // Set a default title if company data is missing
        setReport(prev => ({
          ...prev,
          title: `Internship Report: ${internship.position || 'My Internship'}`
        }));
      }
    }
  }, [existingReport, internship]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setReport(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };
  
  const handleSkillInputChange = (e) => {
    setSkillInput(e.target.value);
  };
  
  const addSkill = () => {
    if (skillInput.trim() === '') return;
    
    // Ensure report.skillsGained is an array
    if (!Array.isArray(report.skillsGained)) {
      setReport(prev => ({
        ...prev,
        skillsGained: [skillInput.trim()]
      }));
    } else if (!report.skillsGained.includes(skillInput.trim())) {
      setReport(prev => ({
        ...prev,
        skillsGained: [...prev.skillsGained, skillInput.trim()]
      }));
    }
    
    setSkillInput('');
  };
  
  const removeSkill = (skill) => {
    if (!Array.isArray(report.skillsGained)) {
      console.error('Cannot remove skill: skillsGained is not an array', report);
      return;
    }
    
    setReport(prev => ({
      ...prev,
      skillsGained: prev.skillsGained.filter(s => s !== skill)
    }));
  };
  
  const addSuggestedSkill = (skill) => {
    if (!Array.isArray(report.skillsGained)) {
      setReport(prev => ({
        ...prev,
        skillsGained: [skill]
      }));
      return;
    }
    
    if (!report.skillsGained.includes(skill)) {
      setReport(prev => ({
        ...prev,
        skillsGained: [...prev.skillsGained, skill]
      }));
    }
  };
  
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Check file types and sizes
    const validFiles = files.filter(file => {
      const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      const maxSize = 5 * 1024 * 1024; // 5MB
      
      if (!validTypes.includes(file.type)) {
        addNotification({
          title: "Invalid File",
          message: `${file.name} is not a supported file type. Please upload PDF or DOC/DOCX files.`,
          type: "error"
        });
        return false;
      }
      
      if (file.size > maxSize) {
        addNotification({
          title: "File Too Large",
          message: `${file.name} exceeds the 5MB limit.`,
          type: "error"
        });
        return false;
      }
      
      return true;
    });
    
    if (validFiles.length > 0) {
      setReport(prev => {
        // Ensure attachments is initialized as an array
        const currentAttachments = Array.isArray(prev.attachments) ? prev.attachments : [];
        return {
          ...prev,
          attachments: [...currentAttachments, ...validFiles]
        };
      });
    }
  };
  
  const removeAttachment = (index) => {
    if (!Array.isArray(report.attachments)) {
      console.error('Cannot remove attachment: attachments is not an array', report);
      return;
    }
    
    setReport(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!report.title || !report.title.trim()) {
      newErrors.title = 'Report title is required';
    }
    
    if (!report.summary || !report.summary.trim() || report.summary.length < 100) {
      newErrors.summary = 'Summary should be at least 100 characters';
    }
    
    if (!report.learningOutcomes || !report.learningOutcomes.trim()) {
      newErrors.learningOutcomes = 'Learning outcomes are required';
    }
    
    if (!report.challenges || !report.challenges.trim()) {
      newErrors.challenges = 'Challenges section is required';
    }
    
    if (!report.solutions || !report.solutions.trim()) {
      newErrors.solutions = 'Solutions section is required';
    }
    
    if (!Array.isArray(report.skillsGained) || report.skillsGained.length === 0) {
      newErrors.skillsGained = 'At least one skill is required';
    }
    
    if (!report.futureApplications || !report.futureApplications.trim()) {
      newErrors.futureApplications = 'Future applications section is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      addNotification({
        title: "Validation Error",
        message: "Please fix the errors in the form before submitting.",
        type: "error"
      });
      return;
    }
    
    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      
      try {
        // Create a copy of the report with file info instead of file objects
        // In a real app, you'd upload the files and get URLs or IDs
        const reportForSaving = {
          ...report,
          attachments: Array.isArray(report.attachments) 
            ? report.attachments.map(file => ({
                name: file.name,
                size: file.size,
                type: file.type,
                lastModified: file.lastModified,
                // In a real app, this would be replaced with a URL or file ID
                url: URL.createObjectURL(file)
              }))
            : []
        };
        
        onSave(reportForSaving);
        
        // Only show notification for non-finalized reports
        if (!report.isFinalized) {
          addNotification({
            title: "Report Saved",
            message: existingReport 
              ? "Your report draft has been updated successfully." 
              : "Your report draft has been saved successfully.",
            type: "success"
          });
        }
      } catch (error) {
        console.error('Error preparing report data:', error);
        addNotification({
          title: "Error",
          message: "An error occurred while processing your report. Please try again.",
          type: "error"
        });
      }
    }, 1500);
  };
  
  const handleFinalize = () => {
    if (!validateForm()) {
      addNotification({
        title: "Validation Error",
        message: "Please fix the errors in the form before finalizing the report.",
        type: "error"
      });
      return;
    }
    
    // Show confirmation dialog
    if (window.confirm('Are you sure you want to finalize this report? Once finalized, you cannot make further changes.')) {
      // Update local state immediately to reflect UI changes
      setReport(prev => ({ ...prev, isFinalized: true }));
      
      setTimeout(() => {
        try {
          // Create a copy of the report with file info instead of file objects
          const reportForSaving = {
            ...report,
            isFinalized: true,
            attachments: Array.isArray(report.attachments) 
              ? report.attachments.map(file => ({
                  name: file.name,
                  size: file.size,
                  type: file.type,
                  lastModified: file.lastModified,
                  url: URL.createObjectURL(file)
                }))
              : []
          };
          
          onSave(reportForSaving);
          
          addNotification({
            title: "Report Finalized",
            message: "Your report has been finalized and submitted for review.",
            type: "success"
          });
        } catch (error) {
          console.error('Error finalizing report:', error);
          addNotification({
            title: "Error",
            message: "An error occurred while finalizing your report. Please try again.",
            type: "error"
          });
          // Revert finalized state in case of error
          setReport(prev => ({ ...prev, isFinalized: false }));
        }
      }, 500);
    }
  };
  
  return (
    <CardWithHeader>
      <div className="card-header">
        <h2>
          {existingReport?.status === 'accepted' 
            ? 'View Internship Report'
            : existingReport?.isFinalized || report.isFinalized
              ? 'Finalized Internship Report'
              : existingReport 
                ? 'Edit Internship Report' 
                : 'New Internship Report'}
        </h2>
        <IconButton onClick={onClose} size="2rem">
          &times;
        </IconButton>
      </div>
      
      <div className="card-content">
        <InternshipInfo>
          <h3>Internship Details</h3>
          <div className="info-grid">
            <div>
              <p><strong>Company:</strong> {internship?.company?.name || 'Not specified'}</p>
              <p><strong>Position:</strong> {internship?.position || 'Not specified'}</p>
            </div>
            <div>
              <p><strong>Duration:</strong> {internship?.duration ? `${internship.duration} months` : 'Not specified'}</p>
              <p><strong>Dates:</strong> {internship?.startDate && internship?.endDate ? 
                `${internship.startDate} to ${internship.endDate}` : 'Not specified'}</p>
            </div>
          </div>
        </InternshipInfo>
        
        <ReportForm onSubmit={handleSubmit}>
          <FormSection>
            <FormGroup>
              <Label htmlFor="title">Report Title</Label>
              <Input 
                type="text" 
                id="title" 
                name="title" 
                value={report.title} 
                onChange={handleChange}
                placeholder="Enter a title for your report"
                disabled={isReadOnly}
              />
              {errors.title && <ErrorMessage>{errors.title}</ErrorMessage>}
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="summary">Executive Summary</Label>
              <Textarea 
                id="summary" 
                name="summary" 
                value={report.summary} 
                onChange={handleChange}
                placeholder="Provide a brief summary of your internship experience, responsibilities, and key achievements"
                rows={4}
                disabled={isReadOnly}
              />
              {errors.summary && <ErrorMessage>{errors.summary}</ErrorMessage>}
              <CharacterCount>Minimum 100 characters. Current: {report.summary.length}</CharacterCount>
            </FormGroup>
          </FormSection>
          
          <FormSection>
            <h3>Learning & Development</h3>
            
            <FormGroup>
              <Label htmlFor="learningOutcomes">Learning Outcomes</Label>
              <Textarea 
                id="learningOutcomes" 
                name="learningOutcomes" 
                value={report.learningOutcomes} 
                onChange={handleChange}
                placeholder="Describe what you learned during your internship. Include technical and soft skills."
                rows={4}
                disabled={isReadOnly}
              />
              {errors.learningOutcomes && <ErrorMessage>{errors.learningOutcomes}</ErrorMessage>}
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="challenges">Challenges Faced</Label>
              <Textarea 
                id="challenges" 
                name="challenges" 
                value={report.challenges} 
                onChange={handleChange}
                placeholder="Describe the main challenges you encountered during your internship."
                rows={4}
                disabled={isReadOnly}
              />
              {errors.challenges && <ErrorMessage>{errors.challenges}</ErrorMessage>}
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="solutions">Solutions & Approaches</Label>
              <Textarea 
                id="solutions" 
                name="solutions" 
                value={report.solutions} 
                onChange={handleChange}
                placeholder="Explain how you addressed the challenges mentioned above."
                rows={4}
                disabled={isReadOnly}
              />
              {errors.solutions && <ErrorMessage>{errors.solutions}</ErrorMessage>}
            </FormGroup>
          </FormSection>
          
          <FormSection>
            <h3>Skills & Applications</h3>
            
            <SkillsContainer>
              <Label>Skills Gained</Label>
              <SkillsList>
                {Array.isArray(report.skillsGained) && report.skillsGained.map((skill, index) => (
                  <SkillTag key={index}>
                    {skill}
                    {!isReadOnly && (
                      <button
                        type="button"
                        onClick={() => removeSkill(skill)}
                      >
                        &times;
                      </button>
                    )}
                  </SkillTag>
                ))}
              </SkillsList>
              
              {!isReadOnly && (
                <SkillInputContainer>
                  <Input 
                    type="text" 
                    value={skillInput} 
                    onChange={handleSkillInputChange}
                    placeholder="Add a skill"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addSkill();
                      }
                    }}
                  />
                  <SecondaryButton 
                    type="button" 
                    onClick={addSkill}
                  >
                    Add
                  </SecondaryButton>
                </SkillInputContainer>
              )}
              
              {errors.skillsGained && <ErrorMessage>{errors.skillsGained}</ErrorMessage>}
              
              {!isReadOnly && (
                <SuggestedSkills>
                  <p>Suggested skills:</p>
                  <div className="suggested-list">
                    {suggestedSkills
                      .filter(skill => !Array.isArray(report.skillsGained) || !report.skillsGained.includes(skill))
                      .slice(0, 10)
                      .map((skill, index) => (
                        <SuggestedSkillButton
                          key={index}
                          type="button"
                          onClick={() => addSuggestedSkill(skill)}
                        >
                          + {skill}
                        </SuggestedSkillButton>
                      ))}
                  </div>
                </SuggestedSkills>
              )}
            </SkillsContainer>
            
            <FormGroup>
              <Label htmlFor="futureApplications">Future Applications</Label>
              <Textarea 
                id="futureApplications" 
                name="futureApplications" 
                value={report.futureApplications} 
                onChange={handleChange}
                placeholder="Explain how you plan to apply what you've learned in your future studies or career."
                rows={4}
                disabled={isReadOnly}
              />
              {errors.futureApplications && <ErrorMessage>{errors.futureApplications}</ErrorMessage>}
            </FormGroup>
          </FormSection>
          
          <FormSection>
            <h3>Feedback & Additional Information</h3>
            
            <FormGroup>
              <Label htmlFor="advisorFeedback">Academic Advisor Feedback (Optional)</Label>
              <Textarea 
                id="advisorFeedback" 
                name="advisorFeedback" 
                value={report.advisorFeedback} 
                onChange={handleChange}
                placeholder="If you've discussed your internship with your academic advisor, include their feedback here."
                rows={4}
                disabled={isReadOnly}
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Attachments</Label>
              {!isReadOnly && (
                <div style={{ marginBottom: '0.75rem' }}>
                  <input 
                    type="file"
                    id="attachments"
                    onChange={handleFileChange}
                    multiple
                    accept=".pdf,.doc,.docx"
                    style={{ display: 'none' }}
                  />
                  <FileUploadButton htmlFor="attachments">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
                      <path d="M7.646 1.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 2.707V11.5a.5.5 0 0 1-1 0V2.707L5.354 4.854a.5.5 0 1 1-.708-.708l3-3z"/>
                    </svg>
                    Upload Files
                  </FileUploadButton>
                  <small style={{ marginLeft: '0.75rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                    (PDF, DOC, DOCX files only, max 5MB each)
                  </small>
                </div>
              )}
              
              {Array.isArray(report.attachments) && report.attachments.length > 0 && (
                <AttachmentsList>
                  {report.attachments.map((file, index) => (
                    <AttachmentItem key={index}>
                      <div className="file-info">
                        <div className="file-icon">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                            <path d="M14 4.5V14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h5.5L14 4.5zm-3 0A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4.5h-2z"/>
                          </svg>
                        </div>
                        <div className="file-details">
                          <strong>{file.name}</strong>
                          <span>{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                        </div>
                      </div>
                      {!isReadOnly && (
                        <button 
                          type="button" 
                          onClick={() => removeAttachment(index)}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                            <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
                          </svg>
                        </button>
                      )}
                    </AttachmentItem>
                  ))}
                </AttachmentsList>
              )}
            </FormGroup>
          </FormSection>
          
          <FormActions>
            {/* Only show Cancel button for reports that are being created (in draft mode) */}
            {!existingReport?.status && !existingReport?.isFinalized && !report.isFinalized && (
              <SecondaryButton type="button" onClick={onClose}>
                Cancel
              </SecondaryButton>
            )}
            
            {/* For accepted reports, only display close button */}
            {existingReport?.status === 'accepted' ? (
              <Button type="button" onClick={onClose}>
                Close
              </Button>
            ) : !existingReport?.isFinalized && !report.isFinalized ? (
              /* For drafts (not finalized), show both save and finalize buttons */
              <>
                <Button type="submit" disabled={isSubmitting || isReadOnly}>
                  {isSubmitting ? 'Saving...' : 'Save'}
                </Button>
                <Button 
                  type="button" 
                  onClick={handleFinalize}
                  disabled={isSubmitting || isReadOnly}
                  style={{ backgroundColor: 'rgba(239, 68, 68, 0.8)' }}
                >
                  Submit and Finalize
                </Button>
              </>
            ) : (
              /* For finalized but not accepted reports, only show close button */
              <Button type="button" onClick={onClose}>
                Close
              </Button>
            )}
          </FormActions>
        </ReportForm>
      </div>
    </CardWithHeader>
  );
};

export default InternshipReport;